import { Input, TablePaginationConfig, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';
import { CallForTenderCards } from '../components/CallForTenderCards';
import { FullscreenSpin } from '../components/FullscreenSpin';
import { useAppDispatch, useAppSelector } from '../hooks/store';
import {
  CallForTender,
  ACTIVE_CFT_MAX_RESULTS,
  searchActiveCallForTenders,
} from '../slices/callForTender';
import { getFinancingNeeds } from '../slices/financingNeed';
import { isOrganizationPremium } from '../utils/projects';

const { Text } = Typography;

const useStyles = createUseStyles({
  container: {
    padding: '89px 61px',
  },
  title: {
    fontFamily: 'PP Mori',
    fontSize: '36px',
    fontWeight: '600',
    lineHeight: '100%',
  },
  searchContainer: {
    marginTop: '24px',
    marginBottom: '24px',
    maxWidth: '400px',
  },
  searchInput: {
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '16px',
    '& .ant-input': {
      fontSize: '16px',
    },
  },
});

export const ActiveCallForTenders: React.VFC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [searchValue, setSearchValue] = useState('');

  const {
    callForTenders,
    loading,
    searchResult,
    user,
    offset,
    financingNeeds,
  } = useAppSelector(
    ({
      callForTender: { callForTenders, loading, searchResult, offset },
      financingNeed: { financingNeeds },
      auth: { user },
    }) => ({
      callForTenders,
      loading,
      searchResult,
      user,
      offset,
      financingNeeds,
    }),
  );

  const isPremium = useMemo<boolean>(
    () => isOrganizationPremium(Object.values(financingNeeds.byId)),
    [financingNeeds.byId],
  );

  useEffect(() => {
    if (user?.ngoId) {
      dispatch(getFinancingNeeds(user.ngoId));
    }
    fetchCallForTenders(0, '');
  }, [dispatch, user?.ngoId]);

  const fetchCallForTenders = (page: number, search: string) => {
    dispatch(
      searchActiveCallForTenders({
        search: search || undefined,
        offset: page * ACTIVE_CFT_MAX_RESULTS,
        limit: ACTIVE_CFT_MAX_RESULTS,
      }),
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (!isPremium) {
      history.push('/ngo/premium');
      return;
    }
    setSearchValue(value);
    fetchCallForTenders(0, value);
  };

  const handlePageChange = (pagination: TablePaginationConfig) => {
    const newPage = (pagination.current ?? 1) - 1;
    if (!isPremium && newPage >= 1) {
      history.push('/ngo/premium');
      return;
    }
    fetchCallForTenders(newPage, searchValue);
  };

  const handlePaywallRedirect = () => {
    history.push('/ngo/premium');
  };

  if (!user) {
    return <FullscreenSpin />;
  }

  const cftList: CallForTender[] | null = searchResult?.resultsIds
    ? searchResult.resultsIds.reduce(
        (list: CallForTender[], cftId) =>
          callForTenders.byId[cftId]
            ? [...list, callForTenders.byId[cftId]]
            : list,
        [],
      )
    : null;

  return (
    <div className={classes.container}>
      <Text className={classes.title}>Programmes de soutien</Text>
      <div className={classes.searchContainer}>
        <Input
          className={classes.searchInput}
          placeholder="Rechercher un programme de soutien..."
          prefix={<SearchOutlined style={{ color: '#737373' }} />}
          value={searchValue}
          onChange={handleSearchChange}
          allowClear
        />
      </div>
      <CallForTenderCards
        callForTenders={cftList}
        onTableChange={handlePageChange}
        loading={loading}
        pagination={{
          total: searchResult?.resultsCount || 0,
          current: offset / ACTIVE_CFT_MAX_RESULTS + 1,
          pageSize: ACTIVE_CFT_MAX_RESULTS,
          showSizeChanger: false,
        }}
        redirectNewPage={true}
        disableClick={!isPremium}
        onPaywallRedirect={handlePaywallRedirect}
      />
    </div>
  );
};
